import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getCoreCommunityUsers,
  removeFromCoreCommunity,
} from "apis/adminServices/userManagmentService";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import AdminModal from "components/admin/AdminModal";
import { User } from "./type";
import AddCoreCommitteeModal from "./addCoreCommitteeModal";
import RowsPerPageSelect from "components/admin/RowsPerPageSelect";

const CoreCommittee: React.FC = () => {
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalMembers, setTotalMembers] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [removing, setRemoving] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();

  const fetchMembers = async (
    page: number,
    size: number,
    showGlobalLoader = true
  ) => {
    try {
      setLoading(true);
      if (showGlobalLoader) showLoader();
      const response = await getCoreCommunityUsers(searchTerm, page, size);
      setMembers(response?.items ?? []);
      setTotalMembers(response?.totalCount ?? 0);
    } catch (error) {
      console.error("Error fetching core community users:", error);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    const isSearch = searchTerm.trim().length > 0;
    fetchMembers(currentPage, pageSize, !isSearch);
  }, [currentPage, pageSize, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(totalMembers / pageSize));

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRemove = (user: User) => {
    setUserToRemove(user);
  };

  const confirmRemove = async () => {
    if (!userToRemove) return;
    try {
      setRemoving(true);
      await removeFromCoreCommunity(userToRemove.userId);
      toast.success("User removed from core committee");
      setUserToRemove(null);
      fetchMembers(currentPage, pageSize);
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      const status = error?.response?.status;
      if (apiMessage) {
        toast.error(apiMessage);
      } else if (status === 400) {
        toast.error("Invalid user ID.");
      } else if (status === 403) {
        toast.error("Cannot modify admin user.");
      } else if (status === 404) {
        toast.error("User not found or not in core community.");
      } else {
        toast.error("Failed to remove user from core committee");
      }
      console.error("Error removing user from core community:", error);
    } finally {
      setRemoving(false);
    }
  };

  const handleAddNew = () => {
    setIsAddModalOpen(true);
  };

  const handleAddSuccess = () => {
    fetchMembers(currentPage, pageSize);
  };

  return (
    <section>
      <div className="bg-white rounded-xl p-4 mt-2">
        <div className="flex flex-wrap gap-4 items-center justify-end pb-4">
          <div className="flex flex-wrap items-center gap-3 relative">
            <div className="relative">
              <input
                className="block w-full p-3 pl-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                placeholder="Search by keyword"
                value={searchTerm}
                onChange={handleSearch}
              />
              <button className="absolute w-fit inset-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                <i className="st-search text-lightgray-200"></i>
              </button>
            </div>
            <button
              type="button"
              className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg"
              onClick={handleAddNew}
            >
              <i className="st-add text-white"></i>
              <p className="font-medium text-white text-sm">Add New</p>
            </button>
          </div>
        </div>

        <div className="relative overflow-x-auto sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 p-4 bg-blue-50">
              <tr>
                <th scope="col" className="px-6 py-4 rounded-l-lg">
                  <p className="text-lightgray-200">User ID</p>
                </th>
                <th scope="col" className="px-6 py-4">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    User Name
                  </p>
                </th>
                <th scope="col" className="px-6 py-4">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Joined Date
                  </p>
                </th>
                <th scope="col" className="px-6 py-4">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Email Address
                  </p>
                </th>
                <th scope="col" className="px-6 py-4">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Status
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 text-center">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Total Tokens
                  </p>
                </th>
                <th scope="col" className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="rounded-lg">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    No core committee members found.
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr
                    key={member?.userId}
                    className="odd:bg-white even:bg-blue-50 border"
                  >
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="pl-3">{member?.userId}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {member?.username
                          ? `${member.username.charAt(0).toUpperCase()}${member.username.slice(1)}`
                          : "UserName"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {member?.joinedDate
                          ? new Date(member.joinedDate).toLocaleDateString("en-GB")
                          : "-"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">{member?.email}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-grease-100 whitespace-nowrap">
                      <p
                        className={`mx-4 px-3 py-1 text-white text-xs rounded-full w-fit ${
                          member?.status === "Active" ? "bg-teal-500" : "bg-red-500"
                        }`}
                      >
                        {member?.status || "unknown"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4 text-center">{member?.totalCredits}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <button
                        onClick={() => handleRemove(member)}
                        className="rounded-md px-4 py-2 text-xs w-20 text-red-500 border border-red-500"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
          <div className="flex flex-wrap items-center gap-5">
            <p className="text-lightgray-200 text-sm">
              Total {totalMembers} {totalMembers === 1 ? "record" : "records"}
            </p>
            <p className="text-lightgray-200 text-sm">Rows per page</p>
            <RowsPerPageSelect
              value={pageSize}
              onChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>

          <div>
            <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
              <li>
                <button
                  disabled={currentPage === 1}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    currentPage === 1
                      ? "text-gray-300 border-gray-300"
                      : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <span className="sr-only">Previous</span>
                  <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                </button>
              </li>

              {generatePageNumbers().map((pageNum) => (
                <li key={pageNum}>
                  <button
                    className={`flex items-center justify-center w-7 h-7 rounded-md ${
                      currentPage === pageNum
                        ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                        : "text-primaryblue-50 border border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li>
                <button
                  disabled={currentPage === totalPages}
                  className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                    currentPage === totalPages
                      ? "text-gray-300 border-gray-300"
                      : "text-primaryblue-50 border-primaryblue-50"
                  }`}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <span className="sr-only">Next</span>
                  <i className="st-arrow-right text-xl font-semibold"></i>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <AddCoreCommitteeModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdded={handleAddSuccess}
      />

      <AdminModal
        isOpen={!!userToRemove}
        onClose={() => {
          if (!removing) setUserToRemove(null);
        }}
        title="Remove from Core Committee"
        size="sm"
        showCloseButton={!removing}
      >
        <div className="flex flex-col items-center justify-center p-2 text-center">
          <p className="mb-6 text-gray-600">
            Are you sure you want to remove{" "}
            <span className="font-semibold text-grease-100">
              {userToRemove?.username || `User ${userToRemove?.userId}`}
            </span>{" "}
            from the core committee?
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => setUserToRemove(null)}
              disabled={removing}
              className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemove}
              disabled={removing}
              className="px-6 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {removing ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Removing...</span>
                </>
              ) : (
                "Remove"
              )}
            </button>
          </div>
        </div>
      </AdminModal>
    </section>
  );
};

export default CoreCommittee;
